import React from 'react';
import { MapPin, Calendar, Moon, Coffee } from 'lucide-react';
import { StorytellingLayout, ImageCarousel, ExpandableCard } from '../base';

/**
 * Tela de storytelling para hotéis
 * 
 * Single Source of Truth: Backend envia todos os dados calculados.
 * Frontend apenas renderiza.
 * 
 * Schema esperado (details):
 * - checkin_date, checkout_date
 * - nights (calculado pelo backend)
 * - room_type, meal_plan
 */
export default function StorytellingHotel({
  block,
  stepIndicator,
  narrativeText,
  ctaText,
  onContinue,
  onBack,
  onShare
}) {
  const details = block.details || {};

  // Monta array de imagens a partir dos campos salvos
  const buildImages = () => {
    const imgs = [];
    if (block.image_url) imgs.push(block.image_url);
    if (details.image_url_2) imgs.push(details.image_url_2);
    if (details.image_url_3) imgs.push(details.image_url_3);

    // Fallback para imagens default se não tiver nenhuma
    if (imgs.length === 0) {
      return [
        'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
        'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&q=80'
      ];
    }
    return imgs;
  };
  const images = buildImages();

  // Formata período de datas
  const formatDateRange = () => {
    const checkinDate = details.checkin_date || block.date;
    const checkoutDate = details.checkout_date;
    
    if (!checkinDate || !checkoutDate) return '';
    
    const checkin = new Date(checkinDate + 'T00:00:00');
    const checkout = new Date(checkoutDate + 'T00:00:00');

    const formatDay = (d) => d.getDate().toString().padStart(2, '0');
    const months = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

    return `${formatDay(checkin)}-${formatDay(checkout)} ${months[checkout.getMonth()]}`;
  };

  // Usa nights do backend (Single Source of Truth)
  const nights = details.nights || 1;

  const cardContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Localização */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem' }}>
          <MapPin size={14} color="rgba(255, 255, 255, 0.7)" strokeWidth={2} />
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(255, 255, 255, 0.7)',
            margin: 0
          }}>
            {details.location || details.address || 'Localização'}
          </p>
        </div>
      </div>

      {/* Detalhes da estadia */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Datas */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
          <Calendar size={18} color="#5DD4C3" strokeWidth={2} />
          <p style={{ fontSize: '0.6875rem', fontWeight: 500, color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            {formatDateRange()}
          </p>
        </div>

        {/* Noites */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
          <Moon size={18} color="#5DD4C3" strokeWidth={2} />
          <p style={{ fontSize: '0.6875rem', fontWeight: 500, color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            {nights} {nights === 1 ? 'NOITE' : 'NOITES'}
          </p>
        </div>

        {/* Regime */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
          <Coffee size={18} color="#5DD4C3" strokeWidth={2} />
          <p style={{ fontSize: '0.6875rem', fontWeight: 500, color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            {(details.meal_plan || 'SEM REFEIÇÃO').toUpperCase()}
          </p>
        </div>
      </div>

        {/* Cancelamento — SOMENTE quando o fornecedor mandou o status estruturado.
            Bloco legado (extracao de PDF) tem cancellation_policy de fidelidade
            nao auditada; publica-lo retroativamente transformaria texto extraido
            por IA em promessa da DSC. O texto, quando aparece, e verbatim. */}
          {details.cancellation_status && details.cancellation_policy ? (
          <div>
            <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.25rem 0' }}>
              Cancelamento
            </p>
            <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, margin: 0 }}>
                {details.cancellation_policy}
            </p>
          </div>
        ) : null}

          {/* Cobranca no destino — sempre que existir. E custo obrigatorio que o
            hospede paga no hotel: omitir seria transferir a surpresa para o
            balcao. Fica FORA do total e nunca e somado a ele. Itemizado, porque
            agregar exigiria criar um numero que nao existe na fonte. */}
          {Array.isArray(details.charges_at_destination)
          && details.charges_at_destination.length > 0 ? (
          <div>
            <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.25rem 0' }}>
              A pagar no hotel — nao incluido no valor da cotacao
            </p>
              {details.charges_at_destination.map((cobranca, idx) => (
              <p key={idx} style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', margin: 0 }}>
                  {cobranca.description}
                  {cobranca.value != null
                  ? ` — R$ ${Number(cobranca.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                  : ''}
              </p>
            ))}
          </div>
        ) : null}
    </div>
  );

  const expandedContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {/* Tipo de quarto */}
      {details.room_type && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.25rem 0' }}>
            Tipo de quarto
          </p>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', margin: 0 }}>
            {details.room_type}
          </p>
        </div>
      )}

      {/* Amenidades */}
      {details.amenities && details.amenities.length > 0 && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.25rem 0' }}>
            Comodidades incluídas
          </p>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, margin: 0 }}>
            {details.amenities.map((item, idx) => (
              <span key={idx}>• {item}<br /></span>
            ))}
          </p>
        </div>
      )}

      {/* Horários */}
      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <div>
          <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>Check-in</p>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.checkin_time || '14:00'}
          </p>
        </div>
        <div>
          <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>Check-out</p>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.checkout_time || '12:00'}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <StorytellingLayout
      backgroundImage={images[0]}
      title={block.title || details.name || 'Hotel'}
      narrativeText={narrativeText}
      stepIndicator={stepIndicator}
      ctaText={ctaText}
      onContinue={onContinue}
      onBack={onBack}
      onShare={onShare}
    >
      <ImageCarousel images={images} />
      <ExpandableCard expandedContent={expandedContent}>
        {cardContent}
      </ExpandableCard>
    </StorytellingLayout>
  );
}
